import { useState, useEffect } from 'react';

import { faCircleNotch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateFactV2 } from '@/api/mutations/useCreateFactV2';
import { useExtractFactsFromImageV2 } from '@/api/mutations/useExtractFactsFromImageV2';
import { FilterOptions } from '@/constants/filterOptions';
import { useUserContext } from '@/Contexts/User';
import { Button } from '@/new/components/shadcn/button';
import { Label } from '@/new/components/shadcn/label';
import { Popover, PopoverAnchor, PopoverContent } from '@/new/components/shadcn/popover';
import { Textarea } from '@/new/components/shadcn/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { BoundingBox } from '@/types';
import { MyOptionType } from '@/types';

interface ExtractedFact {
  description: string;
  author: string;
  topic: string;
  subject: string;
  date_of_event: string;
  source_text: string;
}

interface ExtractFactFromImagePopupProps {
  isOpen: boolean;
  onClose: () => void;
  docId: string;
  imageBase64: string;
  boundingBox: BoundingBox;
  onSuccess: () => void;
  side?: 'left' | 'right';
}

export const ExtractFactFromImagePopup = ({
  isOpen,
  onClose,
  docId,
  imageBase64,
  boundingBox,
  onSuccess,
  side = 'left',
}: ExtractFactFromImagePopupProps) => {
  const { matterId, subsetId } = useParams();

  const { user } = useUserContext();
  const isLabellingOrg = user?.organisation?.isLabellingOrg ?? false;

  const [extractedFacts, setExtractedFacts] = useState<ExtractedFact[]>([]);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [isExtracting, setIsExtracting] = useState(false);

  // Form state for current fact
  const [description, setDescription] = useState('');
  const [significance, setSignificance] = useState('');
  const [factDateText, setFactDateText] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [selectedRelevance, setSelectedRelevance] = useState<MyOptionType | null>({ label: 'Yes', value: 'relevant' });
  const [selectedClassification, setSelectedClassification] = useState<MyOptionType | null>({
    label: 'Primary',
    value: 'primary',
  });
  const [createdFactIndices, setCreatedFactIndices] = useState<number[]>([]);

  const { mutate: extractFacts } = useExtractFactsFromImageV2({
    onSuccess: (data) => {
      setExtractedFacts(data || []);
      setIsExtracting(false);
    },
    onError: (error) => {
      setIsExtracting(false);
      onClose();
    },
  });

  const { mutate: createFact, isLoading: isCreatingFact } = useCreateFactV2({
    matterId,
    subsetId,
    docId,
    onSuccess: () => {
      setCreatedFactIndices([...createdFactIndices, currentFactIndex]);
      handleNextFact();
    },
  });

  const loadFactAtIndex = (index: number) => {
    const fact = extractedFacts[index];
    if (!fact) return;

    if (isLabellingOrg) {
      setDescription('');
      setFactDateText('');
      setSourceText(fact.source_text || '');
      setSignificance('');
      return;
    }

    setDescription(fact.description || '');
    setFactDateText(fact.date_of_event || '');
    setSourceText(fact.source_text || '');
    setSignificance('');
  };

  const handlePreviousFact = () => {
    if (currentFactIndex > 0) {
      const newIndex = currentFactIndex - 1;
      setCurrentFactIndex(newIndex);
      loadFactAtIndex(newIndex);
    }
  };

  const handleNextFact = () => {
    if (currentFactIndex < extractedFacts.length - 1) {
      const newIndex = currentFactIndex + 1;
      setCurrentFactIndex(newIndex);
      loadFactAtIndex(newIndex);
    } else {
      onSuccess();
      onClose();
    }
  };

  const handleSkipFact = () => {
    if (isLastFact) {
      onSuccess();
      onClose();
    } else {
      handleNextFact();
    }
  };

  const handleCreateFact = () => {
    trackCustomEvent(TrackingEventsV2.ConfirmCreateFactFromImageSelection, { matterId, docId });
    createFact({
      description,
      factDateText,
      topic: '',
      significance,
      sourceText,
      relevance: selectedRelevance?.value || 'relevant',
      classification: selectedClassification?.value || 'primary',
      highlightAreas: [boundingBox],
    });
  };

  const handleStartExtraction = () => {
    setIsExtracting(true);
    extractFacts({ matterId: matterId ?? '', docId, imageBase64 });
  };

  const isLastFact = currentFactIndex === extractedFacts.length - 1;
  const isFirstFact = currentFactIndex === 0;

  // Helper function to get button tooltip message
  const getCreateButtonTooltip = () => {
    if (createdFactIndices.includes(currentFactIndex)) {
      return 'This fact has already been created';
    }
    if (!description) return 'Fact description is required';
    if (!sourceText) return 'Source text is required';
    if (!selectedRelevance) return 'Relevance is required';
    if (!selectedClassification) return 'Classification is required';
    return 'Create fact';
  };

  // Start extraction immediately when component mounts
  useEffect(() => {
    if (isOpen && !isExtracting && extractedFacts.length === 0) {
      handleStartExtraction();
    }
  }, [isOpen]);

  // Load first fact when extractedFacts is populated
  useEffect(() => {
    if (extractedFacts.length > 0 && currentFactIndex === 0) {
      loadFactAtIndex(0);
    }
  }, [extractedFacts]);

  const factCreationDisabled = isLabellingOrg
    ? !description || !sourceText || isCreatingFact || createdFactIndices.includes(currentFactIndex)
    : !description ||
      !sourceText ||
      !selectedRelevance ||
      !selectedClassification ||
      isCreatingFact ||
      createdFactIndices.includes(currentFactIndex);

  return (
    <Popover open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <PopoverAnchor />

      <PopoverContent
        side={side}
        align="center"
        sideOffset={8}
        className="max-h-[80vh] min-h-[300px] min-w-xl overflow-y-auto p-4"
        collisionPadding={20}
        avoidCollisions={true}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Extract Facts From Selection</h3>
          <div className="flex items-center gap-3">
            {extractedFacts.length > 0 && !isExtracting && (
              <div className="flex items-center gap-1">
                <button
                  onClick={handlePreviousFact}
                  disabled={isFirstFact}
                  className="flex h-6 w-6 items-center justify-center rounded border border-gray-300 bg-white text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-gray-400"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <span className="px-2 text-sm text-gray-600">
                  {currentFactIndex + 1} / {extractedFacts.length}
                </span>

                <button
                  onClick={handleNextFact}
                  disabled={isLastFact}
                  className="flex h-6 w-6 items-center justify-center rounded border border-gray-300 bg-white text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-gray-400"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}

            <Button onClick={onClose} variant="ghost" size="icon" className="h-7 w-7">
              <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {isExtracting ? (
          <div className="flex h-48 flex-col items-center justify-center gap-3">
            <FontAwesomeIcon icon={faCircleNotch} className="fa-spin text-3xl text-blue-500" />
            <div className="text-xs">Extracting facts from selection...</div>
          </div>
        ) : extractedFacts.length === 0 ? (
          <div className="flex h-24 items-center justify-center">
            <p className="text-xs text-gray-500">No facts could be extracted.</p>
          </div>
        ) : (
          <div className="flex flex-col">
            <div>
              <>
                <Label htmlFor="description" className="text-xs">
                  Fact Description
                </Label>

                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter fact description"
                  className="text-xs"
                />
              </>

              <div>
                <Label htmlFor="sourceText" className="text-xs">
                  Source Text
                </Label>
                <Textarea
                  id="sourceText"
                  value={sourceText}
                  onChange={(e) => setSourceText(e.target.value)}
                  className="text-xs"
                  placeholder="Enter source text"
                />
              </div>

              <div className="flex gap-3">
                <div className="flex-1 space-y-3">
                  <div>
                    <Label htmlFor="factDate" className="text-xs">
                      Date
                    </Label>
                    <input
                      id="factDate"
                      type="text"
                      value={factDateText}
                      onChange={(e) => setFactDateText(e.target.value)}
                      className="h-8 w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                      placeholder="Enter date (optional)"
                    />
                  </div>

                  {!isLabellingOrg && (
                    <div>
                      <Label htmlFor="significance" className="text-xs">
                        Significance
                      </Label>
                      <Textarea
                        id="significance"
                        value={significance}
                        onChange={(e) => setSignificance(e.target.value)}
                        placeholder="Enter significance (optional)"
                        className="text-xs"
                        rows={2}
                      />
                    </div>
                  )}
                </div>

                {!isLabellingOrg && (
                  <div className="w-32 space-y-3 border-l border-dashed border-gray-200 pl-3">
                    <div>
                      <Label htmlFor="relevance" className="text-xs">
                        Relevant
                      </Label>
                      <select
                        id="relevance"
                        className="h-8 w-full rounded border border-gray-300 px-2 text-xs hover:cursor-pointer focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        value={selectedRelevance?.value || 'relevant'}
                        onChange={(e) => {
                          const option = FilterOptions.relevance.find((opt) => opt.value === e.target.value);
                          if (option) setSelectedRelevance(option);
                        }}
                      >
                        {FilterOptions.relevance.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="classification" className="text-xs">
                        Classification
                      </Label>
                      <select
                        id="classification"
                        className="h-8 w-full rounded border border-gray-300 px-2 text-xs hover:cursor-pointer focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        value={selectedClassification?.value || 'primary'}
                        onChange={(e) => {
                          const option = FilterOptions.classification.find((opt) => opt.value === e.target.value);
                          if (option) setSelectedClassification(option);
                        }}
                      >
                        {FilterOptions.classification.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-2 flex items-center justify-between border-t border-gray-200 pt-3">
              <div className="ml-auto flex gap-2">
                <Button
                  onClick={handleSkipFact}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  disabled={isCreatingFact}
                >
                  {isLastFact ? 'Close' : 'Skip'}
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          onClick={handleCreateFact}
                          disabled={factCreationDisabled}
                          size="sm"
                          variant={createdFactIndices.includes(currentFactIndex) ? 'default' : 'primary'}
                        >
                          {isCreatingFact && <FontAwesomeIcon icon={faCircleNotch} className="fa-spin" />}
                          {createdFactIndices.includes(currentFactIndex) ? 'Fact Created' : 'Create Fact'}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">{getCreateButtonTooltip()}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
